import { getPack, type AdoptSignatureRequest, type FormDoc, type LetterDoc } from "@gsignatures/core";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { isFailure } from "../api/types";
import { Icon } from "../components/Icon";
import { SigInk } from "../components/SigInk";
import { TIMING } from "../config";
import { shortHash, shortTime } from "../lib/format";
import { useFocusHeading } from "../lib/hooks";
import type { Answers } from "../state/answers";
import type { FlowCtx } from "../state/flow";
import { AdoptModal } from "../sign/AdoptModal";
import { S } from "../strings";
import { DocViewer } from "../viewer/DocViewer";
import { formOverlay } from "../viewer/FormOverlay";
import { letterOverlay } from "../viewer/LetterOverlay";

interface Props {
  ctx: FlowCtx;
  /** Signed in this order, then the form (whose certification is the last thing signed). */
  letters: LetterDoc[];
  form: FormDoc | undefined;
  answers: Answers | null;
}

type Preview = { key: string; sha256: string; url: string | null };

export function SignScreen({ ctx, letters, form, answers }: Props) {
  const { session, w, api } = ctx;
  const heading = useFocusHeading();
  const sig = session.signature;
  const signed = session.progress.signed;
  const nextLetter = letters.find((l) => !signed[l.id]);
  const lettersSigned = !nextLetter;
  const formSigned = form ? !!signed[form.id] : true;
  const pack = form?.pack ? getPack(form.pack) : null;
  const values = answers?.values ?? form?.values ?? {};

  const [adopting, setAdopting] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [readAll, setReadAll] = useState(false);
  const [justSigned, setJustSigned] = useState<string | null>(null);
  /** Keep a letter on screen a moment after signing it, so the ink is seen landing. */
  const [heldLetter, setHeldLetter] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Record<string, Preview>>({});
  const certSent = useRef(false);
  const certRef = useRef<HTMLDivElement>(null);

  const sigKey = sig ? JSON.stringify(sig) : "";
  const current = nextLetter ? nextLetter.id : form && !formSigned ? form.id : null;

  // Fetch the preview of what's about to be signed — its fingerprint is what the Sign call carries.
  useEffect(() => {
    if (!current || !sig) return;
    if (previews[current]?.key === sigKey) return;
    let live = true;
    api.preview(current).then(
      (p) => {
        if (!live) return;
        const url = p.blob ? URL.createObjectURL(p.blob) : null;
        setPreviews((all) => {
          const old = all[current]?.url;
          if (old) URL.revokeObjectURL(old);
          return { ...all, [current]: { key: sigKey, sha256: p.sha256, url } };
        });
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [api, current, sig, sigKey, previews]);

  const previewsRef = useRef(previews);
  previewsRef.current = previews;
  useEffect(
    () => () => {
      for (const p of Object.values(previewsRef.current)) if (p.url) URL.revokeObjectURL(p.url);
    },
    [],
  );

  const markRead = useCallback(() => {
    setReadAll(true);
    if (form && !certSent.current) {
      certSent.current = true;
      api.certificationShown(form.id).catch(() => {
        certSent.current = false;
      });
    }
  }, [api, form]);

  // A certification short enough to fit without scrolling counts as read.
  useLayoutEffect(() => {
    const el = certRef.current;
    if (!el || readAll) return;
    if (el.clientHeight > 0 && el.scrollHeight <= el.clientHeight + 8) markRead();
  }, [lettersSigned, readAll, markRead]);

  const onCertScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) markRead();
  };

  async function adopt(req: AdoptSignatureRequest): Promise<boolean> {
    const s = await ctx.run(() => api.adoptSignature(req));
    if (!s) return false;
    ctx.update(s);
    ctx.toast(S.adopt.adopted);
    return true;
  }

  async function sign(docId: string) {
    if (busy) return;
    if (!sig) {
      setAdopting(true);
      return;
    }
    setBusy(docId);
    try {
      if (form && docId === form.id && !certSent.current) {
        await ctx.run(() => api.certificationShown(form.id));
        certSent.current = true;
      }
      let sha = previews[docId]?.key === sigKey ? previews[docId]?.sha256 : undefined;
      if (!sha) {
        const p = await ctx.run(() => api.preview(docId));
        if (!p) return;
        sha = p.sha256;
      }
      const s = await ctx.run(async () => {
        try {
          return await api.sign(docId, sha!);
        } catch (e) {
          // A stale preview: forget it, so the next try fetches a fresh one.
          if (isFailure(e) && e.code === "preview_mismatch") setPreviews((all) => ({ ...all, [docId]: { ...all[docId]!, key: "" } }));
          throw e;
        }
      });
      if (!s) return;
      setJustSigned(docId);
      if (letters.some((l) => l.id === docId)) {
        setHeldLetter(docId);
        setTimeout(() => setHeldLetter((h) => (h === docId ? null : h)), TIMING.inkMs);
      }
      ctx.update(s);
      if (s.step.step === "done") setTimeout(() => ctx.apply(s), TIMING.inkMs);
    } finally {
      setBusy(null);
    }
  }

  // The letter being signed — or the one just signed, for a moment — else the form.
  const letter = letters.find((l) => l.id === heldLetter) ?? nextLetter ?? letters[letters.length - 1]!;
  const letterSigned = !!signed[letter.id];
  const showLetter = !lettersSigned || heldLetter !== null || !form || !pack;
  const viewer = showLetter ? (
    <DocViewer
      key={`letter-${letter.id}`}
      title={S.sign.viewerLetter(letter.title)}
      url={api.fileUrl(letter.id)}
      withCredentials={!api.fake}
      pageCount={letter.pages}
      textLayer
      downloadHref={api.fileUrl(letter.id)}
      {...(api.fake ? { demoPath: api.fileUrl(letter.id) } : {})}
      overlay={letterOverlay({
        doc: letter,
        w,
        mode: letterSigned ? "signed" : "sign",
        signature: sig,
        signerName: session.signer.name,
        signedAt: signed[letter.id],
        animate: justSigned === letter.id,
        onFlag: () => void sign(letter.id),
      })}
      focusKey={letterSigned ? "signed" : "sign"}
      focusCenter
    />
  ) : (
    <DocViewer
      key="form"
      title={`${pack!.title} (${S.tax.revision(pack!.revision)})`}
      url={api.fileUrl(form!.id)}
      withCredentials={!api.fake}
      pageCount={1}
      {...(api.fake ? { demoPath: api.fileUrl(form!.id) } : {})}
      overlay={formOverlay({ pack: pack!, values, mode: formSigned ? "signed" : "sign", signature: sig, signedAt: signed[form!.id], animate: justSigned === form!.id })}
      focusKey={formSigned ? "signed" : "sign"}
      focusCenter
    />
  );

  const cert = pack?.certification;
  const formTitle = pack?.title ?? form?.title ?? "";
  const pv = (id: string) => (previews[id]?.key === sigKey ? previews[id] : undefined);

  return (
    <>
      <main className="page" id="main">
        <div className="split">
          <div className="stack">
            <div className="panel panel-pad stack tight-14">
              <div>
                <div className="eyebrow">{S.sign.eyebrow}</div>
                <h1 className="title" ref={heading} tabIndex={-1}>
                  {S.sign.title}
                </h1>
                <p className="lede">{S.sign.lede}</p>
              </div>
              {sig ? (
                <div className="adopted">
                  <span className="sr">{S.sign.yourSignature}</span>
                  <SigInk sig={sig} />
                  <span className="spacer" />
                  {!(lettersSigned && formSigned) && (
                    <button type="button" className="btn-link" onClick={() => setAdopting(true)}>
                      {S.sign.change}
                    </button>
                  )}
                </div>
              ) : (
                <button type="button" className="btn btn-quiet btn-wide" onClick={() => setAdopting(true)}>
                  <Icon n="pen" /> {S.sign.create}
                </button>
              )}
            </div>

            {letters.map((l, i) => {
              const isSigned = !!signed[l.id];
              const locked = !isSigned && l.id !== nextLetter?.id;
              const statement = S.sign.letterStatement(l.title, l.version, w.legal);
              const head = `act-letter-${i + 1}`;
              return (
                <section key={l.id} className={`act ${isSigned ? "signed" : ""} ${locked ? "locked" : ""}`} aria-labelledby={head} aria-disabled={locked || undefined}>
                  <div className="act-head">
                    <span className="act-num" aria-hidden="true">
                      {isSigned ? <Icon n="check" /> : i + 1}
                    </span>
                    <h2 id={head}>{S.sign.letterAct(l.title)}</h2>
                    {isSigned && <span className="chip ok">{S.sign.signedAt(shortTime(signed[l.id]!))}</span>}
                  </div>
                  {!isSigned && (
                    <div className="act-body" inert={locked}>
                      {locked && <p className="help">{S.sign.signFirst(letters.slice(0, i).filter((x) => !signed[x.id]).map((x) => x.title))}</p>}
                      <p className="muted act-text">
                        {statement.before}
                        <b>{statement.button}</b>
                        {statement.after}
                      </p>
                      {pv(l.id) && (
                        <p className="fingerprint">
                          <Icon n="fingerprint" /> <span className="mono">{S.sign.fingerprint(shortHash(pv(l.id)!.sha256))}</span>
                          {pv(l.id)!.url && (
                            <a className="inline-link" href={pv(l.id)!.url!} target="_blank" rel="noreferrer">
                              {S.sign.seeExact}
                            </a>
                          )}
                        </p>
                      )}
                      <button type="button" className="btn btn-primary" disabled={locked || !sig || busy !== null} onClick={() => void sign(l.id)} aria-busy={busy === l.id}>
                        <Icon n="pen" /> {busy === l.id ? S.sign.signing : S.sign.letterButton(l.title)}
                      </button>
                      {!sig && !locked && <span className="help">{S.sign.createFirst}</span>}
                    </div>
                  )}
                </section>
              );
            })}

            {form && cert && (
              <section className={`act ${formSigned ? "signed" : ""} ${lettersSigned ? "" : "locked"}`} aria-labelledby="act-form" aria-disabled={!lettersSigned || undefined}>
                <div className="act-head">
                  <span className="act-num" aria-hidden="true">
                    {formSigned ? <Icon n="check" /> : letters.length + 1}
                  </span>
                  <h2 id="act-form">{S.sign.act2(formTitle)}</h2>
                  {formSigned && <span className="chip ok">{S.sign.signedAt(shortTime(signed[form.id]!))}</span>}
                </div>
                {!formSigned && (
                  <div className="act-body" inert={!lettersSigned}>
                    {!lettersSigned && <p className="help">{S.sign.signFirst(letters.filter((x) => !signed[x.id]).map((x) => x.title))}</p>}
                    <div className="perjury" ref={certRef} tabIndex={0} onScroll={onCertScroll} role="region" aria-label={S.sign.certLabel} aria-describedby="cert-hint">
                      <p>
                        <b>{cert.title}</b>
                      </p>
                      {cert.paragraphs.map((p) => (
                        <p key={p}>{p}</p>
                      ))}
                      <ul>
                        {cert.bullets.map((b) => (
                          <li key={b}>{b}</li>
                        ))}
                      </ul>
                      {cert.closing.map((p) => (
                        <p key={p}>{p}</p>
                      ))}
                    </div>
                    <div className={`scroll-hint ${readAll ? "ok" : ""}`} id="cert-hint" aria-live="polite">
                      <Icon n={readAll ? "check" : "scroll"} /> {readAll ? S.sign.readAll : S.sign.scrollHint}
                    </div>
                    <p className="declaration">
                      <b>{cert.declaration}</b>
                    </p>
                    {pv(form.id) && (
                      <p className="fingerprint">
                        <Icon n="fingerprint" /> <span className="mono">{S.sign.fingerprint(shortHash(pv(form.id)!.sha256))}</span>
                        {pv(form.id)!.url && (
                          <a className="inline-link" href={pv(form.id)!.url!} target="_blank" rel="noreferrer">
                            {S.sign.seeExact}
                          </a>
                        )}
                      </p>
                    )}
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!readAll || !lettersSigned || !sig || busy !== null}
                      onClick={() => void sign(form.id)}
                      aria-busy={busy === form.id}
                    >
                      <Icon n="pen" /> {busy === form.id ? S.sign.signing : S.sign.act2Button(formTitle)}
                    </button>
                  </div>
                )}
              </section>
            )}

            <button type="button" className="btn btn-quiet self-start" onClick={() => ctx.go("review")}>
              <Icon n="back" /> {S.sign.backToReview}
            </button>
          </div>
          <div className="sticky-col">{viewer}</div>
        </div>
      </main>
      {adopting && <AdoptModal name={session.signer.name} current={sig} onClose={() => setAdopting(false)} onAdopt={adopt} />}
    </>
  );
}
